import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, GitBranch, Users2, User2, ChevronLeft, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "---";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "---"; }
}

export default function Genealogy() {
  const { user } = useAuth();
  const [stack, setStack] = useState([]); // breadcrumbs: [{user_code, name}]
  const [node, setNode] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (code) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/genealogy/${encodeURIComponent(code)}`);
      setNode(data.node);
      setChildren(data.children);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.user_code) return;
    setStack([{ user_code: user.user_code, name: user.name || "You" }]);
    load(user.user_code);
  }, [user?.user_code, load]);

  const drillInto = (child) => {
    const next = [...stack, { user_code: child.user_code, name: child.name }];
    setStack(next);
    load(child.user_code);
  };

  const goBack = () => {
    if (stack.length <= 1) return;
    const next = stack.slice(0, -1);
    setStack(next);
    load(next[next.length - 1].user_code);
  };

  const jumpTo = (idx) => {
    const next = stack.slice(0, idx + 1);
    setStack(next);
    load(next[next.length - 1].user_code);
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4 pb-16">
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-1 text-red-700 font-semibold text-sm"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>

        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-red-700 font-bold text-xl flex items-center gap-2"
              style={{ fontFamily: "Outfit, sans-serif" }}>
            <GitBranch size={20} /> Genealogy
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Tap any user to view their direct downline.
          </p>

          {/* Breadcrumbs */}
          <div
            data-testid="genealogy-breadcrumbs"
            className="mt-4 flex items-center flex-wrap gap-1 text-sm bg-slate-50 border border-slate-200 rounded-lg p-2"
          >
            {stack.map((s, i) => {
              const last = i === stack.length - 1;
              return (
                <span key={s.user_code} className="flex items-center gap-1">
                  <button
                    data-testid={`crumb-${s.user_code}`}
                    onClick={() => !last && jumpTo(i)}
                    className={`px-2 py-1 rounded-md font-semibold ${
                      last
                        ? "bg-red-700 text-white cursor-default"
                        : "text-red-700 hover:bg-red-50"
                    }`}
                  >
                    {s.name} <span className="opacity-70 font-normal">· {s.user_code}</span>
                  </button>
                  {!last && <ChevronRight size={14} className="text-slate-400" />}
                </span>
              );
            })}
          </div>

          {/* Current node */}
          {node && (
            <div
              data-testid="genealogy-current-node"
              className="mt-4 rounded-xl p-4 bg-gradient-to-br from-red-600 to-rose-800 text-white"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-white/20 flex items-center justify-center">
                  <User2 size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-lg truncate">{node.name}</div>
                  <div className="text-xs opacity-90 truncate">
                    {node.user_code} · {node.rank}
                  </div>
                </div>
                <span
                  className={`text-[11px] px-2 py-1 rounded-full font-bold shrink-0 ${
                    node.activated ? "bg-emerald-500/90" : "bg-slate-700/70"
                  }`}
                >
                  {node.activated ? "Active" : "Pending"}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div className="opacity-90">
                  <div className="opacity-70">Join Date</div>
                  <div className="font-semibold">{formatDate(node.join_date)}</div>
                </div>
                <div className="opacity-90">
                  <div className="opacity-70">Direct Downline</div>
                  <div className="font-semibold">{children.length}</div>
                </div>
              </div>
            </div>
          )}

          {/* Back one level */}
          {stack.length > 1 && (
            <button
              data-testid="genealogy-back-level"
              onClick={goBack}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-red-700"
            >
              <ChevronLeft size={16} /> Back one level
            </button>
          )}
        </section>

        {/* Children list */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <Users2 size={16} className="text-red-700" />
            <div className="font-bold text-slate-900">
              Direct Downline <span className="text-slate-500 font-semibold">({children.length})</span>
            </div>
          </div>
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="animate-spin text-red-600" size={32} />
            </div>
          ) : children.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="genealogy-no-downline">
              <Users2 size={36} className="mx-auto text-slate-300 mb-2" />
              <div className="font-semibold text-slate-700">No direct downline</div>
              <div className="text-sm mt-1">This user has no direct referrals yet.</div>
            </div>
          ) : (
            <ul data-testid="genealogy-children-list" className="divide-y divide-slate-100">
              {children.map((c) => (
                <li key={c.user_code}>
                  <button
                    data-testid={`genealogy-child-${c.user_code}`}
                    onClick={() => drillInto(c)}
                    className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 text-left"
                  >
                    <div className="h-10 w-10 shrink-0 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold">
                      {c.name?.slice(0, 1).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold shrink-0 ${
                            c.activated
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {c.activated ? "Active" : "Pending"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {c.user_code} · {c.rank}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Joined: <span className="font-medium text-slate-700">{formatDate(c.join_date)}</span>
                        {c.has_downline && (
                          <span className="ml-2 text-red-700 font-semibold">· has downline</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={18} className="text-slate-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
